import React from "react";
import { PilotPropertyCard } from "../../pages/PropertiesPage";

/* ─────────────────────────────────────────────────────────────
   MarketingProperties · Phase 2 Homepage
   ------------------------------------------------------------
   Composes the REAL production `PilotPropertyCard` from
   /pages/PropertiesPage.jsx with a curated 4-property portfolio.
   No modifications to the production card. Card click surfaces
   (property modal open + Deal Room <Link/>) are intercepted at
   the wrapper level so the marketing page cleanly scrolls to
   the Deal Room section rather than triggering unmounted modals
   or navigating to a demo deal that doesn't exist in Postgres.

   Uses the same field shape the real page produces via
   `_toCardFromUserProperty()` — nothing invented.
   ───────────────────────────────────────────────────────────── */

const DEMO_PROPERTIES = [
  {
    id: "mkt-p-01",
    dealId: null,
    address: "23 Hallings Wharf, Stratford, London",
    postcode: "E15 2GX",
    type: "Apartment",
    beds: 2, baths: 2,
    price: "£645,000",
    status: "SALES AGREED",
    progression: "Enquiries · Management pack overdue",
    step: 3, stepTotal: 6,
    updated: "2h ago",
    exch: "Exch · 6 Mar",
    opState: "Needs attention",
    assignees: 1, parking: null, parkingSpaces: null,
    tenure: "leasehold",
    thumb: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=900&q=80&auto=format&fit=crop",
  },
  {
    id: "mkt-p-02",
    dealId: null,
    address: "14 Priory Lane, Wimbledon",
    postcode: "SW19 5HH",
    type: "House",
    beds: 4, baths: 3,
    price: "£1,285,000",
    status: "SALES AGREED",
    progression: "Awaiting solicitor response",
    step: 4, stepTotal: 6,
    updated: "1d ago",
    exch: "Exch · 1 Mar",
    opState: "At risk",
    assignees: 1, parking: "Driveway", parkingSpaces: 2,
    tenure: "freehold",
    thumb: "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=900&q=80&auto=format&fit=crop",
  },
  {
    id: "mkt-p-03",
    dealId: null,
    address: "11 Church Road, Barnes",
    postcode: "SW13 9HH",
    type: "House",
    beds: 5, baths: 3,
    price: "£1,975,000",
    status: "SALES AGREED",
    progression: "Ready for exchange",
    step: 5, stepTotal: 6,
    updated: "just now",
    exch: "Exch · 4 Mar",
    opState: "On track",
    assignees: 1, parking: "Garage", parkingSpaces: 1,
    tenure: "freehold",
    thumb: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=900&q=80&auto=format&fit=crop",
  },
  {
    id: "mkt-p-04",
    dealId: null,
    address: "8 Park Road, Ealing",
    postcode: "W13 8QF",
    type: "House",
    beds: 3, baths: 2,
    price: "£845,000",
    status: "UNDER OFFER",
    progression: "TA6 outstanding",
    step: 2, stepTotal: 6,
    updated: "3d ago",
    exch: "Exch · TBC",
    opState: "Needs attention",
    assignees: 1, parking: null, parkingSpaces: null,
    tenure: "freehold",
    thumb: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=900&q=80&auto=format&fit=crop",
  },
];

/* Card interception — the production PilotPropertyCard binds an
   onClick that opens the PropertyDetailsModal, and a nested
   <Link/> that routes to /deal-rooms/{dealId}. On the marketing
   page we want a card click to scroll to the Deal Room section
   below (which shows the same 23 Hallings Wharf transaction
   opened). We intercept in the capture phase so the underlying
   handlers never fire — the production component is untouched. */
const scrollToDealRoom = () => {
  const el = document.getElementById("the-deal-room");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const MarketingProperties = () => (
  <section
    id="properties"
    className="relative"
    style={{ background: "#FAFAFE" }}
    data-testid="landing-properties"
  >
    <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-10 pt-16 lg:pt-24 pb-14 lg:pb-20">
      {/* Copy block — restrained, left-aligned, generous whitespace */}
      <div className="max-w-[720px]">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-violet-700">
          Your pipeline at a glance
        </p>
        <h2 className="font-display text-[32px] sm:text-[40px] lg:text-[52px] font-bold tracking-[-0.028em] text-slate-900 leading-[1.02] mt-4">
          Every property.<br />
          <span
            style={{
              background: "linear-gradient(90deg, #4F46E5 0%, #6D28D9 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            One live view.
          </span>
        </h2>
        <p className="text-[14px] sm:text-[15.5px] text-slate-600 leading-relaxed mt-5 max-w-[560px]">
          See every transaction, its stage and what needs attention —
          without piecing updates together across multiple systems.
        </p>
      </div>

      {/* Real product cards — 1 → 2 → 4 responsive grid.
          onClickCapture prevents the production card's modal/link
          firing on the marketing page. */}
      <div
        className="mt-10 lg:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5"
        onClickCapture={(e) => {
          e.preventDefault();
          e.stopPropagation();
          scrollToDealRoom();
        }}
        onKeyDownCapture={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            scrollToDealRoom();
          }
        }}
        data-testid="landing-properties-grid"
      >
        {DEMO_PROPERTIES.map((p) => (
          <PilotPropertyCard key={p.id} p={p} />
        ))}
      </div>
    </div>
  </section>
);

export default MarketingProperties;
